import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { LoginFooter } from '@/components/LoginFooter';
import { LogIn, UserPlus, ArrowRight } from 'lucide-react';

interface WelcomeScreenProps {
    onLogin: () => void;
    onSignup: () => void;
    teamName?: string;
}

export function WelcomeScreen({ onLogin, onSignup, teamName }: WelcomeScreenProps) {
    return (
        <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4 relative overflow-hidden">
            {/* Background decoration */}
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/5 blur-3xl opacity-50" />
                <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] rounded-full bg-accent/5 blur-3xl opacity-50" />
            </div>

            <div className="z-10 w-full max-w-md space-y-8 animate-fade-in">
                <div className="text-center space-y-4">
                    <div className="mx-auto mb-8">
                        <img src="/logo-112.webp" alt="Logo" className="w-28 h-28 mx-auto shadow-xl rounded-2xl" fetchPriority="high" width={112} height={112} />
                    </div>

                    <h1 className="text-4xl font-display font-bold text-foreground tracking-tight">
                        {teamName ? `Welkom bij ${teamName}` : 'Welkom bij Clando'}
                    </h1>

                    <p className="text-lg text-muted-foreground leading-relaxed">
                        {teamName
                            ? 'Je bent uitgenodigd om deel te nemen aan dit team. Log in of maak een account aan om te beginnen.'
                            : 'De slimme assistent voor jouw huishouden. Beheer taken, boodschappen en meer op één plek.'}
                    </p>
                </div>

                <Card className="border-border/50 shadow-xl bg-card/50 backdrop-blur-sm">
                    <CardContent className="p-6 space-y-4">
                        <Button
                            className="w-full h-12 text-base font-medium shadow-lg hover:shadow-xl transition-all duration-300 group"
                            size="lg"
                            onClick={onSignup}
                        >
                            <UserPlus className="w-5 h-5 mr-2 group-hover:scale-110 transition-transform" />
                            Nieuw account maken
                            <ArrowRight className="w-4 h-4 ml-2 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                        </Button>

                        <Button
                            variant="outline"
                            className="w-full h-12 text-base bg-background/50 hover:bg-background/80 border-primary/20 hover:border-primary/50 transition-all duration-300"
                            size="lg"
                            onClick={onLogin}
                        >
                            <LogIn className="w-5 h-5 mr-2" />
                            Inloggen
                        </Button>
                    </CardContent>
                </Card>

                {/* Feature highlights - subtle */}
                {!teamName && (
                    <div className="grid grid-cols-3 gap-2 text-center text-xs text-muted-foreground/60">
                        <div className="flex flex-col items-center gap-1">
                            <span className="p-2 rounded-full bg-primary/5">✨</span>
                            <span>Slim plannen</span>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <span className="p-2 rounded-full bg-primary/5">🔄</span>
                            <span>Gedeeld</span>
                        </div>
                        <div className="flex flex-col items-center gap-1">
                            <span className="p-2 rounded-full bg-primary/5">📱</span>
                            <span>Altijd bij de hand</span>
                        </div>
                    </div>
                )}
            </div>

            <div className="mt-12 z-10">
                <LoginFooter />
            </div>
        </div>
    );
}
